import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { ArrowLeft, TrendingUp, TrendingDown, Star, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CrossSellAnalytics from "@/components/admin/CrossSellAnalytics";
import GoogleReviewsImport from "@/components/admin/GoogleReviewsImport";

/**
 * Admin Analytics — dedicated /admin/analytics page with 3 widgets:
 *   1. Revenue over time (line)
 *   2. Top routes by revenue (horizontal bar)
 *   3. Guest satisfaction (avg trend + rating distribution)
 *
 * iter75 — the "Driver payouts" widget was retired along with the rest
 * of the in-app payout math; the operator reads payouts directly from
 * Google Sheets now.
 *
 * Time-range chip selector applies across all widgets. Each widget calls
 * its own endpoint so a slow one never blocks the others.
 */
export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [source, setSource] = useState("ALL");
  const [revenue, setRevenue] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [sat, setSat] = useState(null);

  const load = async (d, src) => {
    // Fire the three in parallel — each widget handles its own loading state.
    const params = { days: d };
    try {
      const [r, t, s] = await Promise.allSettled([
        api.get("/admin/analytics/revenue", { params: { ...params, source: src } }),
        api.get("/admin/analytics/top-routes", { params: { ...params, limit: 8 } }),
        api.get("/admin/analytics/satisfaction", { params }),
      ]);
      setRevenue(r.status === "fulfilled" ? r.value.data : null);
      setRoutes(t.status === "fulfilled" ? t.value.data : null);
      setSat(s.status === "fulfilled" ? s.value.data : null);
    } catch (e) {
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => { load(days, source); }, [days, source]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur bg-zinc-950/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin/dashboard" data-testid="analytics-back"
                className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#C5A059]">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="text-right">
            <h1 className="font-display text-2xl">Analytics</h1>
            <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Last {days} days
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Range chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="range-chips">
          {[
            { d: 7, label: "7 days" },
            { d: 30, label: "30 days" },
            { d: 90, label: "90 days" },
            { d: 365, label: "1 year" },
          ].map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              data-testid={`range-${r.d}`}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                days === r.d
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Source filter chips — filter revenue by booking origin */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="source-chips">
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 self-center mr-1">
            Source
          </span>
          {[
            { s: "ALL",     label: "All" },
            { s: "PRIVATE", label: "Private (website)" },
            { s: "UBER",    label: "Uber" },
            { s: "BOLT",    label: "Bolt" },
          ].map((r) => (
            <button
              key={r.s}
              onClick={() => setSource(r.s)}
              data-testid={`source-${r.s}`}
              className={`px-3.5 py-1 rounded-full text-[10px] uppercase tracking-wider border transition-colors ${
                source === r.s
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 1 · Revenue over time */}
          <Card testid="widget-revenue" title="Revenue over time" icon={TrendingUp}
                headerRight={<RevenueDelta totals={revenue?.totals} />}>
            <RevenueChart data={revenue} />
          </Card>

          {/* 2 · Top routes */}
          <Card testid="widget-routes" title="Top routes by revenue" icon={MapPin}>
            <TopRoutesChart data={routes} />
          </Card>

          {/* 3 · Guest satisfaction */}
          <Card testid="widget-satisfaction" title="Guest satisfaction" icon={Star} wide>
            <SatisfactionChart data={sat} />
          </Card>
        </div>

        {/* Conversion & Performance */}
        <div className="mt-10" data-testid="conversion-performance-section">
          <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#C5A059]" /> Conversion &amp; Performance
          </h2>
          <CrossSellAnalytics />
        </div>

        {/* Reviews & Reputation */}
        <div className="mt-10" data-testid="reviews-reputation-section">
          <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
            <Star className="w-4 h-4 text-[#C5A059]" /> Reviews &amp; Reputation
          </h2>
          <GoogleReviewsImport />
        </div>
      </div>
    </div>
  );
}

/* -------------------- shared -------------------- */

function Card({ title, icon: Icon, headerRight, children, testid, wide }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-white/5 bg-zinc-900/50 p-5 ${wide ? "lg:col-span-2" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400">
          <Icon className="w-4 h-4 text-[#C5A059]" />
          {title}
        </div>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="h-56 flex items-center justify-center text-zinc-600 text-sm">
      {label}
    </div>
  );
}

/* -------------------- widgets -------------------- */

function RevenueDelta({ totals }) {
  if (!totals) return null;
  const pct = totals.revenue_change_pct;
  const trendClass = pct == null ? "text-zinc-500"
    : pct >= 0 ? "text-emerald-400" : "text-red-400";
  const TrendIcon = pct == null ? null : pct >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="text-right">
      <div className="text-white font-display text-lg">€{Number(totals.revenue_eur || 0).toFixed(0)}</div>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${trendClass}`}>
        {TrendIcon && <TrendIcon className="w-3 h-3" />}
        {pct == null ? "—" : `${pct >= 0 ? "+" : ""}${pct}% vs prev`}
      </div>
    </div>
  );
}

function RevenueChart({ data }) {
  const series = data?.series || [];
  if (!series.length) return <EmptyState label="No completed rides in this window" />;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={series} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="revStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#C5A059" />
            <stop offset="100%" stopColor="#D4AF37" />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="bucket" tick={{ fill: "#71717a", fontSize: 11 }} />
        <YAxis tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
        <Tooltip
          contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }}
          labelStyle={{ color: "#a1a1aa" }}
          formatter={(v, n) => n === "revenue" ? [`€${v}`, "Revenue"] : [v, "Bookings"]}
        />
        <Line type="monotone" dataKey="revenue" stroke="url(#revStroke)" strokeWidth={2.5}
              dot={{ r: 3, fill: "#C5A059" }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function TopRoutesChart({ data }) {
  const rows = data?.routes || [];
  if (!rows.length) return <EmptyState label="No routes yet in this window" />;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 20, left: 20, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
        <YAxis type="category" dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 11 }} width={180} />
        <Tooltip
          contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }}
          formatter={(v, n, p) => n === "revenue"
            ? [`€${v} · ${p.payload.count} ride${p.payload.count === 1 ? "" : "s"}`, "Revenue"]
            : [v, n]}
        />
        <Bar dataKey="revenue" fill="#C5A059" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}


function SatisfactionChart({ data }) {
  const trend = data?.trend || [];
  const dist = data?.distribution || [];
  const overall = data?.overall;
  const totalDist = useMemo(() => dist.reduce((s, d) => s + d.count, 0), [dist]);
  const has = trend.length > 0 || totalDist > 0;
  if (!has) return <EmptyState label="No guest ratings yet" />;

  const DIST_COLORS = ["#dc2626", "#f97316", "#f59e0b", "#84cc16", "#10b981"];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Avg trend</span>
          {overall?.avg && (
            <span className="text-white font-display text-xl">
              {overall.avg.toFixed(2)}
              <span className="text-xs text-zinc-500 ml-2">
                · {overall.count} rating{overall.count === 1 ? "" : "s"}
              </span>
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} />
            <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fill: "#71717a", fontSize: 11 }} />
            <Tooltip
              contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => n === "avg" ? [`${v}★`, "Avg"] : [v, "Count"]}
            />
            <Line type="monotone" dataKey="avg" stroke="#C5A059" strokeWidth={2.5} dot={{ r: 3, fill: "#C5A059" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Rating distribution</div>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={dist} dataKey="count" nameKey="stars"
                 innerRadius={40} outerRadius={70} paddingAngle={3}>
              {dist.map((d, i) => (
                <Cell key={i} fill={DIST_COLORS[Math.max(0, d.stars - 1)]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8, fontSize: 12 }}
              formatter={(v, n, p) => [`${v} ratings`, `${p.payload.stars}★`]}
            />
            <Legend
              iconSize={8}
              wrapperStyle={{ fontSize: 10, color: "#71717a", paddingTop: 8 }}
              formatter={(v, e) => `${e.payload.stars}★ · ${e.payload.count}`}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
